import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Chip,
  Stack,
  TextField
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import PhonebookContactAutocomplete from "../phonebook/phonebookAutocomplete/PhonebookContactAutocomplete";
import { UserContext } from "../contexts/UserContext";
import { Contact } from "../orderEntry/types/Contact";
import { ContactOption } from "../phonebook/phonebookAutocomplete/ContactOption";
import { prependUserToSelectNote } from "../utils/prependUserToSelectNote";
import {
  buildOrderedDetailsNote,
  buildSurveyZoningReasonNote,
  buildWaitingOnOneOrMoreQuotesNote,
  buildWaitingOnClientChoiceNote,
  buildFollowedUpWithClientNote,
  buildWaitingOnClientSignatureNote,
  buildSignatureRemindedNote,
  buildWaitingOnProposalNote,
  buildProposalRemindedNote,
  buildSurveyorContactedNote,
  buildReceivedDateNote,
  buildReceivedDateClearedNote,
  buildSurveyZoningStatusNote,
  buildWaitingForQuotesNote,
  getOrderedDetailsContactName,
  SurveyZoningOrderedDetails
} from "./orderedDetails";
import { CloseIcon } from "../../minimals/theme/overrides/CustomIcons";
import { RequestedTaskDescription, RequestedTaskStatusOption } from "./types";

interface Props {
  currentContactEmail?: string | null;
  currentContactName?: string | null;
  currentEta?: string | null;
  currentStatus?: string | null;
  currentCompany?: string | null;
  currentStatusNote?: string | null;
  currentDisplayStatus?: string | null;
  existingOrderedDetails?: SurveyZoningOrderedDetails | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
  orderNumber: string;
  orderNumberDisplay: string;
  quoteRequestedAt?: string | Date | null;
  quoteWaitingStatus?: "Waiting for quotes" | "Waiting on 1+ quote" | null;
  clientChoiceRequestedAt?: string | Date | null;
  clientFollowUpAt?: string | Date | null;
  signatureRequestedAt?: string | Date | null;
  signatureReminderAt?: string | Date | null;
  proposalRequestedAt?: string | Date | null;
  proposalReminderAt?: string | Date | null;
  receivedDate?: string | null;
  statusOptions: RequestedTaskStatusOption[];
  taskDescriptions: RequestedTaskDescription[];
}

type ExistingOrderContact = {
  code?: string | null;
  contactType?: string | null;
  lookupCode?: string | null;
  name?: string | null;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  payeeName?: string | null;
  type?: string | null;
};

type ChecklistTaskItem = {
  description?: string | null;
  status?: string | null;
};

type TitleChargeAmountResponse = {
  description?: string | null;
  chargeAmount?: number | string | null;
};

const COMMITMENT_CHECKLIST_TASK_DESCRIPTION = "06. Send out Commitment";
const normalizeComparableText = (value?: string | null) => value?.trim().replace(/\s+/g, " ").toLowerCase() || "";
const hasUsableContactLabel = (contact: { name?: string | null; company?: string | null }) => {
  const label = contact.name?.trim() || contact.company?.trim() || "";
  return Boolean(label && label !== "-");
};
const getTitleChargeDescription = (taskDescription?: string | null) =>
  taskDescription === "Zoning Report" ? "Zoning" : "Survey";

export default function EditRequestedTaskStatusModal({
  currentContactEmail,
  currentContactName,
  currentEta,
  currentStatus,
  currentCompany,
  currentStatusNote,
  currentDisplayStatus,
  existingOrderedDetails,
  isOpen,
  onClose,
  onSaved,
  orderNumber,
  orderNumberDisplay,
  quoteRequestedAt,
  quoteWaitingStatus,
  clientChoiceRequestedAt,
  clientFollowUpAt,
  signatureRequestedAt,
  signatureReminderAt,
  proposalRequestedAt,
  proposalReminderAt,
  receivedDate,
  statusOptions,
  taskDescriptions
}: Props) {
  const { user } = useContext(UserContext);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const matchedStatus = statusOptions.find((s) => currentStatus === s.fieldDisplayName || currentStatus === s.value) || null;
  const [selectedStatus, setSelectedStatus] = useState<RequestedTaskStatusOption | null>(
    matchedStatus || statusOptions[0] || null
  );
  const [selectedEta, setSelectedEta] = useState<Date | null>(
    currentEta ? new Date(currentEta) : existingOrderedDetails?.eta ? new Date(existingOrderedDetails.eta) : null
  );
  const [selectedReceivedDate, setSelectedReceivedDate] = useState<Date | null>(
    receivedDate ? new Date(`${receivedDate}T00:00:00`) : null
  );
  const includesSurvey = useMemo(() => taskDescriptions.includes("Survey"), [taskDescriptions]);
  const initialContactName = currentContactName?.trim() || existingOrderedDetails?.contactName || "";
  const initialContactEmail = currentContactEmail?.trim() || existingOrderedDetails?.contactEmail || "";
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>(
    hasUsableContactLabel({ name: initialContactName })
      ? [{
          id: null,
          company: initialContactName,
          email: initialContactEmail || null,
          lookupCode: existingOrderedDetails?.contactLookupCode || null,
          name: initialContactName,
          suffix: null,
          salesRep: null,
          nyAccountRep: null,
          njAccountRep: null,
          nationalAccountRep: null,
          njCommercialAccountRep: null,
          internalDriverEmail: null,
          internalDriverPhone: null,
          clientInstructions: null,
          whoHasTheFile: null
        } as Contact]
      : []
  );
  const selectedContact = selectedContacts[0] || null;
  const [contactOptions, setContactOptions] = useState<Contact[]>([]);
  const [contactSearch, setContactSearch] = useState("");
  const [amount, setAmount] = useState("");
  const [commitmentNotReady, setCommitmentNotReady] = useState(false);
  const [naReason, setNaReason] = useState("");
  const [manualQuoteNote, setManualQuoteNote] = useState("");
  const [companyName, setCompanyName] = useState(currentCompany || "");
  const [statusNote, setStatusNote] = useState(currentStatusNote || "");

  useEffect(() => {
    if (isOpen) {
      setCompanyName(currentCompany || "");
      setStatusNote(currentStatusNote || "");
    }
  }, [currentCompany, currentStatusNote, isOpen]);

  const primaryTaskDescription = taskDescriptions[0];
  const titleChargeDescription = getTitleChargeDescription(primaryTaskDescription);
  const titleChargeAmountEndpoint =
    titleChargeDescription === "Survey"
      ? "/proxy/api/orders/GetSurveyTitleChargeAmount"
      : "/proxy/api/orders/GetZoningTitleCharge";
  const orderContactType = primaryTaskDescription === "Survey" ? "Surveyor" : "Zoning";
  const phonebookApiOptions = primaryTaskDescription === "Survey" ? { contactType: "Surveyor" } : {};
  const isWaitingForQuotesStatus = selectedStatus?.fieldDisplayName === "Waiting for quotes";
  const isManualWaitingForQuotesStatus = selectedStatus?.fieldDisplayName === "Waiting on 1+ quote";
  const isWaitingOnClientChoiceStatus = selectedStatus?.fieldDisplayName === "Waiting on client choice";
  const isWaitingOnClientSignatureStatus = selectedStatus?.fieldDisplayName === "Waiting on client signature";
  const isWaitingOnProposalStatus = selectedStatus?.fieldDisplayName === "Waiting on proposal";
  const isSurveyorContactedStatus = selectedStatus?.fieldDisplayName === "Surveyor contacted";
  const isReceivedStatus = selectedStatus?.fieldDisplayName === "Received";
  const isReceivedNeedInvoiceStatus = selectedStatus?.fieldDisplayName === "Received, need invoice";
  const requiresOrderedDetails = selectedStatus?.fieldDisplayName === "Ordered";
  const shouldShowQuotedAmount = Boolean(
    requiresOrderedDetails ||
      isWaitingForQuotesStatus ||
      isManualWaitingForQuotesStatus ||
      isReceivedNeedInvoiceStatus
  );
  const proposalFollowUpAvailable = Boolean(
    (proposalReminderAt && (() => {
      const remindedAt = new Date(proposalReminderAt);
      const oneAm = new Date(remindedAt);
      oneAm.setHours(1, 0, 0, 0);
      if (remindedAt >= oneAm) oneAm.setDate(oneAm.getDate() + 1);
      const fourPm = new Date(remindedAt);
      fourPm.setHours(16, 0, 0, 0);
      if (remindedAt >= fourPm) fourPm.setDate(fourPm.getDate() + 1);
      return Date.now() >= Math.min(oneAm.getTime(), fourPm.getTime());
    })()) ||
      (!proposalReminderAt && proposalRequestedAt && (() => {
        const requestedAt = new Date(proposalRequestedAt);
        const oneAm = new Date(requestedAt);
        oneAm.setHours(1, 0, 0, 0);
        if (requestedAt >= oneAm) oneAm.setDate(oneAm.getDate() + 1);
        const fourPm = new Date(requestedAt);
        fourPm.setHours(16, 0, 0, 0);
        if (requestedAt >= fourPm) fourPm.setDate(fourPm.getDate() + 1);
        return Date.now() >= Math.min(oneAm.getTime(), fourPm.getTime());
      })())
  );
  const signatureFollowUpAvailable = Boolean(
    (signatureReminderAt && new Date(signatureReminderAt).getTime() <= Date.now() - 48 * 60 * 60 * 1000) ||
      (!signatureReminderAt && signatureRequestedAt && (() => {
        const requestedAt = new Date(signatureRequestedAt);
        const oneAm = new Date(requestedAt);
        oneAm.setHours(1, 0, 0, 0);
        if (requestedAt >= oneAm) oneAm.setDate(oneAm.getDate() + 1);
        const fourPm = new Date(requestedAt);
        fourPm.setHours(16, 0, 0, 0);
        if (requestedAt >= fourPm) fourPm.setDate(fourPm.getDate() + 1);
        return Date.now() >= Math.min(oneAm.getTime(), fourPm.getTime());
      })())
  );
  const clientChoiceFollowUpAvailable = Boolean(
    (clientFollowUpAt && new Date(clientFollowUpAt).getTime() <= Date.now() - 72 * 60 * 60 * 1000) ||
      (!clientFollowUpAt &&
        clientChoiceRequestedAt &&
        new Date(clientChoiceRequestedAt).getTime() <= Date.now() - 48 * 60 * 60 * 1000)
  );
  const isSecondClientFollowUp = Boolean(clientFollowUpAt);
  const quoteUpdateRequestAvailable = Boolean(
    quoteWaitingStatus &&
      quoteRequestedAt &&
      new Date(quoteRequestedAt).getTime() <= Date.now() - 24 * 60 * 60 * 1000
  );
  const contactLabel =
    taskDescriptions.length === 1 && taskDescriptions[0] !== "Survey"
      ? "Zoning Contact"
      : "Surveyor / Zoning Contact";

  useEffect(() => {
    if (!isOpen || !orderNumber) {
      return;
    }

    let cancelled = false;
    const loadOrderContact = async () => {
      try {
        const { data } = await axios.get<ExistingOrderContact[]>(
          `/proxy/api/orders/GetOrderContacts?orderNumber=${encodeURIComponent(orderNumber)}`
        );
        const savedContacts = (Array.isArray(data) ? data : []).filter(
          (contact) =>
            (contact.type || contact.contactType || "").toLowerCase() === orderContactType.toLowerCase() &&
            hasUsableContactLabel(contact)
        );

        if (cancelled || !savedContacts.length) {
          return;
        }

        const contacts = savedContacts.map((savedContact) => {
          const contactName = savedContact.name?.trim() || savedContact.company?.trim() || "";
          return {
            id: null,
            code: savedContact.code || null,
            company: savedContact.company || contactName,
            email: savedContact.email || null,
            lookupCode: savedContact.lookupCode || null,
            name: savedContact.name || contactName,
            payeeName: savedContact.payeeName || savedContact.company || contactName,
            phone: savedContact.phone || null,
            suffix: null,
            salesRep: null,
            nyAccountRep: null,
            njAccountRep: null,
            nationalAccountRep: null,
            njCommercialAccountRep: null,
            internalDriverEmail: null,
            internalDriverPhone: null,
            clientInstructions: null,
            whoHasTheFile: null
          } as Contact;
        });
        setSelectedContacts(contacts);
      } catch {
        // The row contact remains available if the order-contact lookup fails.
      }
    };

    loadOrderContact();
    return () => {
      cancelled = true;
    };
  }, [isOpen, orderNumber, orderContactType]);

  useEffect(() => {
    if (!isOpen || !includesSurvey) {
      return;
    }

    const abortController = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await axios.get<Contact[]>("/api/clientphonebook/contacts/getOrderEntryContacts", {
          params: { searchText: contactSearch, contactType: "Surveyor" },
          signal: abortController.signal
        });
        if (!abortController.signal.aborted) {
          setContactOptions(
            Array.isArray(data) ? data.filter((contact) => contact.isIndividual && hasUsableContactLabel(contact)) : []
          );
        }
      } catch (error) {
        if (!axios.isCancel(error) && !abortController.signal.aborted) {
          setContactOptions([]);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      abortController.abort();
    };
  }, [contactSearch, includesSurvey, isOpen]);

  const normalizeChargeAmountForInput = (value: unknown) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value.toString() : "";
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) {
        return "";
      }

      const normalized = trimmed.replace(/[$,\s]/g, "");
      return Number.isFinite(Number(normalized)) ? normalized : trimmed;
    }

    return "";
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!includesSurvey) {
      setCommitmentNotReady(false);
      return;
    }

    let isCurrent = true;

    const loadCommitmentChecklistTask = async () => {
      try {
        const { data } = await axios.get<ChecklistTaskItem[]>(
          `/proxy/api/checklisttasks/get?ordernumber=${encodeURIComponent(orderNumber)}`
        );
        const checklistTasks = Array.isArray(data) ? data : [];
        const commitmentTask = checklistTasks.find(
          (task) =>
            task.description?.trim().toLowerCase() === COMMITMENT_CHECKLIST_TASK_DESCRIPTION.toLowerCase()
        );

        if (!isCurrent) {
          return;
        }

        if (commitmentTask) {
          setCommitmentNotReady(commitmentTask.status === "Required");
          return;
        }

        setCommitmentNotReady(false);
      } catch {
        if (!isCurrent) {
          return;
        }

        setCommitmentNotReady(false);
      }
    };

    void loadCommitmentChecklistTask();

    return () => {
      isCurrent = false;
    };
  }, [includesSurvey, isOpen, orderNumber]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isCurrent = true;

    const loadTitleChargeAmount = async () => {
      try {
        const { data } = await axios.get<TitleChargeAmountResponse>(titleChargeAmountEndpoint, {
          params: {
            orderNumber
          }
        });

        if (!isCurrent) {
          return;
        }

        const normalizedAmount = normalizeChargeAmountForInput(data?.chargeAmount);
        if (normalizedAmount) {
          setAmount(normalizedAmount);
        }
      } catch {
        // Leave the amount empty when Select cannot return the charge.
      }
    };

    void loadTitleChargeAmount();

    return () => {
      isCurrent = false;
    };
  }, [isOpen, orderNumber, titleChargeAmountEndpoint]);

  const getRequestedTaskStatusValue = () => {
    switch (selectedStatus?.fieldDisplayName) {
      case "Waiting for quotes":
        return "Required";
      case "Waiting on 1+ quote":
        return "Required";
      case "Waiting on client choice":
        return "Required";
      case "Waiting on client signature":
        return "Required";
      case "Waiting on proposal":
        return "Required";
      case "Surveyor contacted":
        return "Requested";
      case "Ordered":
        return "Requested";
      case "Received":
      case "Received, need invoice":
        return "Received";
      case "N/A":
        return "NA";
      default:
        return null;
    }
  };

  const saveRequestedTaskStatus = async (status: string) => {
    for (const taskDescription of taskDescriptions) {
      await axios.post("/proxy/api/requestedtasks/update", {
        orderNumber,
        task: {
          description: taskDescription,
          status
        }
      });
    }
  };

  const saveEta = async () => {
    for (const taskDescription of taskDescriptions) {
      await axios.post("/proxy/api/requestedtasks/update", {
        orderNumber,
        task: {
          description: taskDescription,
          dueDate: selectedEta
        }
      });
    }
  };

  const saveOrderedDetailsNote = async () => {
    if (!user) {
      throw new Error("Unable to determine the current user.");
    }

    const noteText = buildOrderedDetailsNote({
      contact: selectedContact
    });

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(noteText, user.name)
    });
  };

  const shouldSaveOrderedDetailsNote = () => {
    if (!selectedContact) {
      return false;
    }

    const nextRequestedFromName = normalizeComparableText(getOrderedDetailsContactName(selectedContact));
    if (!nextRequestedFromName) {
      return false;
    }

    const existingRequestedFromName = normalizeComparableText(existingOrderedDetails?.contactName);
    if (!existingRequestedFromName) {
      return true;
    }

    return existingRequestedFromName !== nextRequestedFromName;
  };

  const saveSurveyTitleCharge = async () => {
    const normalizedAmountText = amount.trim().replace(/[$,\s]/g, "");
    const chargeAmount = Number(normalizedAmountText);

    if (!normalizedAmountText || !Number.isFinite(chargeAmount)) {
      throw new Error("Please enter a valid amount.");
    }

    await axios.post("/proxy/api/orders/AddSurveyTitleCharge", {
      orderNumber,
      chargeAmount,
      description: titleChargeDescription
    });
  };

  const saveCommitmentChecklistTask = async () => {
    if (!includesSurvey || !commitmentNotReady || !user?.username) {
      return;
    }

    await axios.post("/proxy/api/orders/UpdateChecklistTaskStatus", {
      OrderNumber: orderNumber,
      Status: "Required",
      TaskDescription: COMMITMENT_CHECKLIST_TASK_DESCRIPTION,
      CompletedByUserName: user.username
    });
  };

  const saveOrderContacts = async () => {
    if (!selectedContacts.length) {
      throw new Error("Unable to determine the selected contact.");
    }

    const isOtherOrderContactType = orderContactType === "Zoning";
    const { data } = await axios.get<ExistingOrderContact[]>(
      `/proxy/api/orders/GetOrderContacts?orderNumber=${encodeURIComponent(orderNumber)}`
    );
    for (const selectedContact of selectedContacts) {
      const existingContact = (Array.isArray(data) ? data : []).find(
        (contact) =>
          (contact.type === orderContactType || contact.contactType === orderContactType) &&
          !!contact.lookupCode &&
          !!selectedContact.lookupCode &&
          contact.lookupCode.toLowerCase() === selectedContact.lookupCode.toLowerCase()
      );
      if (existingContact) {
        continue;
      }

      const effectiveLookupCode = selectedContact.lookupCode || null;
      const phonebookContact = Boolean(effectiveLookupCode);
      const contactName = getOrderedDetailsContactName(selectedContact);
      const payeeName = selectedContact.payeeName?.trim() || selectedContact.company?.trim() || contactName;
      const email = selectedContact.emailsSplit?.length
        ? selectedContact.emailsSplit.filter(Boolean).join(";")
        : selectedContact.email;

      await axios.post("/proxy/api/orders/addOrderContact", {
        orderNumber,
        contact: {
          lookupCode: effectiveLookupCode,
          name: selectedContact.name?.trim() || contactName,
          phone: selectedContact.phone,
          email,
          company: selectedContact.company?.trim() || payeeName,
          payeeName,
          type: orderContactType,
          contactType: orderContactType,
          isOther: isOtherOrderContactType || !phonebookContact
        }
      });
    }
  };

  const saveWaitingForQuotesNote = async () => {
    if (!user) {
      throw new Error("Unable to determine the current user.");
    }

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildWaitingForQuotesNote(companyName), user.name)
    });
  };

  const saveManualWaitingForQuotesNote = async () => {
    const noteText = manualQuoteNote.trim();
    if (!noteText) {
      return;
    }

    if (!user) {
      throw new Error("Unable to determine the current user.");
    }

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(noteText, user.name)
    });
  };

  const saveManualWaitingForQuotesStatusNote = async () => {
    if (!user) {
      throw new Error("Unable to determine the current user.");
    }

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildWaitingOnOneOrMoreQuotesNote(companyName), user.name)
    });
  };

  const saveWaitingOnClientChoiceNote = async () => {
    if (!user) {
      throw new Error("Unable to determine the current user.");
    }

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildWaitingOnClientChoiceNote(), user.name)
    });
  };

  const saveWaitingOnClientSignatureNote = async () => {
    if (!user) throw new Error("Unable to determine the current user.");
    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildWaitingOnClientSignatureNote(companyName), user.name)
    });
  };

  const saveWaitingOnProposalNote = async () => {
    if (!user) throw new Error("Unable to determine the current user.");
    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildWaitingOnProposalNote(companyName), user.name)
    });
  };

  const saveSurveyorContacted = async () => {
    if (!user) throw new Error("Unable to determine the current user.");
    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote("Contacted surveyor requesting expected delivery", user.name)
    });
    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildSurveyorContactedNote(), user.name)
    });
  };

  const saveProposalFollowUp = async () => {
    if (!user) throw new Error("Unable to determine the current user.");
    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(
        proposalReminderAt ? "Requested proposal again" : "Requested Proposal",
        user.name
      )
    });
    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildProposalRemindedNote(), user.name)
    });
  };

  const followUpOnProposal = async () => {
    setSubmitting(true);
    setErrorMessage("");
    try {
      await saveProposalFollowUp();
      await onSaved();
      onClose();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Unable to follow up on the proposal.");
    } finally {
      setSubmitting(false);
    }
  };

  const saveSignatureFollowUp = async () => {
    if (!user) throw new Error("Unable to determine the current user.");
    const noteText = signatureReminderAt
      ? "Reminded client again to Sign proposal"
      : "Reminded client to Sign proposal";
    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(noteText, user.name)
    });
    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildSignatureRemindedNote(), user.name)
    });
  };

  const followUpOnSignature = async () => {
    setSubmitting(true);
    setErrorMessage("");
    try {
      await saveSignatureFollowUp();
      await onSaved();
      onClose();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Unable to follow up with the client.");
    } finally {
      setSubmitting(false);
    }
  };

  const saveClientFollowUp = async () => {
    if (!user) {
      throw new Error("Unable to determine the current user.");
    }

    const noteText = isSecondClientFollowUp
      ? "Followed up again with client"
      : "Followed up with Client";
    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(noteText, user.name)
    });

    if (isSecondClientFollowUp) {
      await saveRequestedTaskStatus("NA");
      await saveStatusTrackingNote("N/A");
      return;
    }

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildFollowedUpWithClientNote(), user.name)
    });
  };

  const followUpWithClient = async () => {
    setSubmitting(true);
    setErrorMessage("");
    try {
      await saveClientFollowUp();
      await onSaved();
      onClose();
    } catch (error) {
      setErrorMessage(
        axios.isAxiosError(error)
          ? typeof error.response?.data === "string"
            ? error.response.data
            : error.response?.data?.message || "Unable to follow up with the client."
          : error instanceof Error
          ? error.message
          : "Unable to follow up with the client."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const saveQuoteUpdateRequested = async () => {
    if (!user || !quoteWaitingStatus) {
      throw new Error("Unable to determine the current user or waiting status.");
    }

    const { data } = await axios.get<ExistingOrderContact[]>(
      `/proxy/api/orders/GetOrderContacts?orderNumber=${encodeURIComponent(orderNumber)}`
    );
    const contactTypes = new Set<"Surveyor" | "Zoning">(
      taskDescriptions.map((taskDescription) => (taskDescription === "Survey" ? "Surveyor" : "Zoning"))
    );
    const contactNames = (Array.isArray(data) ? data : [])
      .filter((contact) => contactTypes.has((contact.type || contact.contactType || "") as "Surveyor" | "Zoning"))
      .map((contact) => contact.name?.trim() || contact.company?.trim() || "")
      .filter(Boolean);
    const names = [...new Set(contactNames)];
    const requestedFrom = names.length ? names.map((name) => `"${name}"`).join(", ") : '"selected contact"';

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(`Quote update requested from ${requestedFrom}`, user.name)
    });

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(
        quoteWaitingStatus === "Waiting on 1+ quote"
          ? buildWaitingOnOneOrMoreQuotesNote()
          : buildWaitingForQuotesNote(),
        user.name
      )
    });
  };

  const requestQuoteUpdate = async () => {
    setSubmitting(true);
    setErrorMessage("");
    try {
      await saveQuoteUpdateRequested();
      await onSaved();
      onClose();
    } catch (error) {
      setErrorMessage(
        axios.isAxiosError(error)
          ? typeof error.response?.data === "string"
            ? error.response.data
            : error.response?.data?.message || "Unable to request a quote update."
          : error instanceof Error
          ? error.message
          : "Unable to request a quote update."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const saveStatusTrackingNote = async (status: "Ordered" | "Received" | "Received, need invoice" | "N/A") => {
    if (!user) {
      throw new Error("Unable to determine the current user.");
    }

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildSurveyZoningStatusNote(status), user.name)
    });
  };

  const saveNaReason = async () => {
    const reason = naReason.trim();
    if (!reason) {
      return;
    }

    if (!user) {
      throw new Error("Unable to determine the current user.");
    }

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(`Reason for N/A: ${reason}`, user.name)
    });
  };

  const saveReceivedDate = async () => {
    if (!user || (!selectedReceivedDate && !receivedDate)) {
      return;
    }

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(
        selectedReceivedDate ? buildReceivedDateNote(selectedReceivedDate) : buildReceivedDateClearedNote(),
        user.name
      )
    });
  };

  const saveStatusNote = async () => {
    const note = statusNote.trim();
    if (currentDisplayStatus !== "Overdue" || !note || !user) return;

    await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
      orderNumber,
      taskDescription: taskDescriptions[0],
      text: prependUserToSelectNote(buildSurveyZoningReasonNote(note), user.name)
    });
  };

  const save = async () => {
    if (!selectedStatus) {
      setErrorMessage("Please choose a status.");
      return;
    }

    if (requiresOrderedDetails && (!selectedEta || !selectedContact || !amount.trim())) {
      setErrorMessage("ETA, contact, and amount are required when moving this to Ordered.");
      return;
    }
    setSubmitting(true);
    setErrorMessage("");

    try {
      if (requiresOrderedDetails) {
        await saveRequestedTaskStatus("Requested");
        await saveEta();
        await saveOrderContacts();
        await saveSurveyTitleCharge();
        await saveCommitmentChecklistTask();
        if (shouldSaveOrderedDetailsNote()) {
          await saveOrderedDetailsNote();
        }
        if (currentStatus !== "Ordered") {
          await saveStatusTrackingNote("Ordered");
        }
      } else {
        const requestedTaskStatus = getRequestedTaskStatusValue();
        if (requestedTaskStatus) {
          await saveRequestedTaskStatus(requestedTaskStatus);
        }

        if (shouldShowQuotedAmount && amount.trim()) {
          await saveSurveyTitleCharge();
        }

        if (isManualWaitingForQuotesStatus) {
          await saveManualWaitingForQuotesStatusNote();
          await saveManualWaitingForQuotesNote();
        } else if (isWaitingOnClientChoiceStatus) {
          await saveWaitingOnClientChoiceNote();
        } else if (isWaitingOnClientSignatureStatus) {
          await saveWaitingOnClientSignatureNote();
        } else if (isWaitingOnProposalStatus) {
          await saveWaitingOnProposalNote();
        } else if (isSurveyorContactedStatus) {
          await saveSurveyorContacted();
        } else if (isWaitingForQuotesStatus && currentStatus !== "Waiting for quotes") {
          await saveWaitingForQuotesNote();
        } else if (
          (selectedStatus?.fieldDisplayName === "Received" ||
            selectedStatus?.fieldDisplayName === "Received, need invoice") &&
          currentStatus !== selectedStatus.fieldDisplayName
        ) {
          await saveStatusTrackingNote(selectedStatus.fieldDisplayName as "Received" | "Received, need invoice");
        } else if (selectedStatus?.fieldDisplayName === "N/A" && currentStatus !== "N/A") {
          await saveStatusTrackingNote("N/A");
        }

        if (isReceivedStatus) {
          await saveReceivedDate();
        }

        if (selectedStatus?.fieldDisplayName === "N/A") {
          await saveNaReason();
        }
      }

      await saveStatusNote();

      await onSaved();
      onClose();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(
          typeof error.response?.data === "string"
            ? error.response.data
            : error.response?.data?.message || "Unable to save the status update."
        );
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Unable to save the status update.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center" marginBottom={3}>
          <Box>Edit Status for Order {orderNumberDisplay}</Box>
        </Stack>
      </DialogTitle>
      <IconButton
        onClick={onClose}
        disabled={submitting}
        sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ paddingBottom: 3 }}>
        <Box paddingTop={1}>
          {errorMessage && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
            <Autocomplete
              size="small"
              options={statusOptions}
              value={selectedStatus}
              renderInput={(params) => <TextField {...params} label="Status" />}
              onChange={(_, value) => setSelectedStatus(value)}
              fullWidth
              sx={{ flex: 1, minWidth: 0 }}
              getOptionLabel={(option) => option.fieldDisplayName}
            />
            {quoteUpdateRequestAvailable && (
              <Button variant="outlined" size="small" onClick={requestQuoteUpdate} disabled={submitting} sx={{ flexShrink: 0 }}>
                Requested
              </Button>
            )}
            {clientChoiceFollowUpAvailable && (
              <Button variant="outlined" size="small" onClick={followUpWithClient} disabled={submitting} sx={{ flexShrink: 0 }}>
                {isSecondClientFollowUp ? "Followed Up" : "Followed up"}
              </Button>
            )}
            {signatureFollowUpAvailable && (
              <Button variant="outlined" size="small" onClick={followUpOnSignature} disabled={submitting} sx={{ flexShrink: 0 }}>
                {signatureReminderAt ? "Followed Up" : "Followed up"}
              </Button>
            )}
            {proposalFollowUpAvailable && (
              <Button variant="outlined" size="small" onClick={followUpOnProposal} disabled={submitting} sx={{ flexShrink: 0 }}>
                {proposalReminderAt ? "Followed Up" : "Followed up"}
              </Button>
            )}
          </Stack>
          {isReceivedStatus && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ marginTop: 2 }}>
              <DatePicker
                label="Received date"
                value={selectedReceivedDate}
                onChange={setSelectedReceivedDate}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSelectedReceivedDate(null)}
                disabled={!selectedReceivedDate || submitting}
              >
                Clear
              </Button>
            </Stack>
          )}
          {currentDisplayStatus === "Overdue" && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              margin="dense"
              label="Overdue note"
              placeholder="Example: Waiting on commitment from client"
              value={statusNote}
              onChange={(event) => setStatusNote(event.target.value)}
              sx={{ marginTop: 2 }}
            />
          )}
          {requiresOrderedDetails && (
            <Stack spacing={2} marginTop={2}>
              <DatePicker
                label="ETA"
                value={selectedEta}
                onChange={setSelectedEta}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              {includesSurvey ? (
                <Autocomplete
                  multiple
                  size="small"
                  options={contactOptions}
                  value={selectedContacts}
                  filterSelectedOptions
                  getOptionLabel={(option) => option.name || option.company || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.lookupCode === value.lookupCode || option.name === value.name
                  }
                  onChange={(_, value) => setSelectedContacts(value)}
                  inputValue={contactSearch}
                  onInputChange={(_, value) => setContactSearch(value)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.lookupCode || option.name || index}
                        label={option.name || option.company || ""}
                      />
                    ))
                  }
                  renderOption={(props, option) => <ContactOption props={props} option={option} />}
                  renderInput={(params) => <TextField {...params} label="Surveyors" placeholder="Search surveyors" />}
                  disabled={submitting}
                />
              ) : (
                <PhonebookContactAutocomplete
                  apiOptions={phonebookApiOptions}
                  contact={selectedContact}
                  required={false}
                  setContact={(value) => setSelectedContacts(value ? [value] : [])}
                  showAddOption={false}
                  textboxLabel={contactLabel}
                  textboxPlaceholder=""
                />
              )}
              {includesSurvey && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={commitmentNotReady}
                      onChange={(event) => setCommitmentNotReady(event.target.checked)}
                    />
                  }
                  label="Commitment not ready"
                />
              )}
            </Stack>
          )}
          {shouldShowQuotedAmount && (
            <TextField
              fullWidth
              label="Quoted amount"
              onChange={(event) => setAmount(event.target.value)}
              size="small"
              value={amount}
              sx={{ marginTop: 2 }}
            />
          )}
          {(isWaitingForQuotesStatus || isManualWaitingForQuotesStatus || isWaitingOnClientSignatureStatus || isWaitingOnProposalStatus) && (
            <TextField
              fullWidth
              size="small"
              label={isWaitingForQuotesStatus || isManualWaitingForQuotesStatus ? "Company / companies" : "Company"}
              placeholder="Company name"
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value)}
              sx={{ marginTop: 2 }}
            />
          )}
          {isManualWaitingForQuotesStatus && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              margin="dense"
              label="Note"
              placeholder={'Quotes Received from "Company Name"'}
              value={manualQuoteNote}
              onChange={(event) => setManualQuoteNote(event.target.value)}
              sx={{ marginTop: 2 }}
            />
          )}
          {selectedStatus?.fieldDisplayName === "N/A" && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              margin="dense"
              label="Reason for N/A (optional)"
              value={naReason}
              onChange={(event) => setNaReason(event.target.value)}
              sx={{ marginTop: 2 }}
            />
          )}
          <Stack direction="row" justifyContent="right" marginTop={2}>
            <Button variant="contained" size="small" onClick={save} disabled={submitting || !selectedStatus}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
